// ════════════════════════════════════════════════════════════════════════════
// Public Booking API (No Auth Required)
// ════════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL ?? "";

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface BusinessInfo {
  name: string;
  email: string;
  phone?: string;
  timezone: string;
  logoUrl?: string;
  branding: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    accentColor: string;
    extractedColors?: string[];
  };
  bookingWindowDays: number;
  depositEnabled: boolean;
  depositType: "flat" | "percentage";
  depositAmount: string;
  hidePricing?: boolean;
  termsOfServiceUrl?: string | null;
  privacyPolicyUrl?: string | null;
}

export interface Location {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  timezone: string;
}

export interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  price: string;
  imageUrl?: string;
}

export interface ServiceCategory {
  id: string | null;
  name: string;
  services: Service[];
}

export interface Provider {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  photoUrl?: string;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface BookingAppointment {
  id: string;
  confirmationCode: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  clientNotes?: string;
  client: {
    name: string;
    email: string;
    phone?: string;
  };
  service: {
    name: string;
    durationMinutes: number;
    price: string;
  };
  provider: {
    name: string;
  };
  location: {
    name: string;
    address?: string;
    timezone: string;
  };
}

export interface BookingResponse {
  appointment: BookingAppointment;
  links: {
    googleCalendar: string;
    icsDownload: string;
  };
  business: {
    name: string;
    email: string;
    phone?: string;
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// API Functions
// ──────────────────────────────────────────────────────────────────────────────

async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error ?? `API error: ${res.status}`);
  }

  return res.json();
}

export async function getBusinessInfo(slug: string): Promise<BusinessInfo> {
  return publicFetch<BusinessInfo>(`/api/public/${slug}/info`);
}

export async function getLocations(slug: string): Promise<Location[]> {
  const response = await publicFetch<{ locations: Location[] }>(
    `/api/public/${slug}/locations`
  );
  return response.locations;
}

export async function getServices(
  slug: string,
  locationId?: string
): Promise<ServiceCategory[]> {
  const params = locationId ? `?locationId=${locationId}` : "";
  const response = await publicFetch<{ categories: ServiceCategory[] }>(
    `/api/public/${slug}/services${params}`
  );
  return response.categories;
}

export async function getProviders(
  slug: string,
  serviceId: string,
  locationId?: string
): Promise<Provider[]> {
  const params = new URLSearchParams({ serviceId });
  if (locationId) params.set("locationId", locationId);
  const response = await publicFetch<{ providers: Provider[] }>(
    `/api/public/${slug}/providers?${params.toString()}`
  );
  return response.providers;
}

export async function getAvailableDays(
  slug: string,
  serviceId: string,
  providerId: string,
  locationId: string,
  month: string // YYYY-MM
): Promise<string[]> {
  const params = new URLSearchParams({
    serviceId,
    providerId,
    locationId,
    date: month,
  });

  // Only enable debug mode in development
  const isDev = import.meta.env.DEV;
  if (isDev) {
    params.set("debug", "true");
  }

  const response = await publicFetch<{ month: string; availableDays: string[]; debug?: unknown[] }>(
    `/api/public/${slug}/availability?${params.toString()}`
  );

  // Log debug info in development if no days are available
  if (isDev && response.availableDays.length === 0 && response.debug) {
    console.warn("[Availability Debug] No available days found. Debug info:", response.debug);
    console.warn("[Availability Debug] Params:", { serviceId, providerId, locationId, month });
  }

  return response.availableDays;
}

export async function getAvailableSlots(
  slug: string,
  serviceId: string,
  providerId: string,
  locationId: string,
  date: string // YYYY-MM-DD
): Promise<TimeSlot[]> {
  const params = new URLSearchParams({
    serviceId,
    providerId,
    locationId,
    date,
  });
  const response = await publicFetch<{ date: string; slots: TimeSlot[] }>(
    `/api/public/${slug}/availability?${params.toString()}`
  );
  return response.slots;
}

export interface CreateBookingData {
  serviceId: string;
  providerId: string;
  locationId: string;
  date: string;
  startTime: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  clientNotes?: string;
  paymentIntentId?: string; // Include if deposit was paid
  smsOptIn?: boolean; // Client consented to SMS reminders
}

export interface PaymentIntentResponse {
  depositRequired: boolean;
  depositAmount?: number;
  clientSecret?: string;
  paymentIntentId?: string;
}

export async function createPaymentIntent(
  slug: string,
  serviceId: string,
  clientEmail?: string
): Promise<PaymentIntentResponse> {
  return publicFetch<PaymentIntentResponse>(
    `/api/public/${slug}/create-payment-intent`,
    {
      method: "POST",
      body: JSON.stringify({ serviceId, clientEmail }),
    }
  );
}

export async function createBooking(
  slug: string,
  data: CreateBookingData
): Promise<BookingResponse> {
  return publicFetch<BookingResponse>(`/api/public/${slug}/book`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function lookupAppointment(
  slug: string,
  confirmationCode: string
): Promise<BookingResponse> {
  return publicFetch<BookingResponse>(
    `/api/public/${slug}/appointment/${confirmationCode}`
  );
}

export async function cancelAppointment(
  slug: string,
  confirmationCode: string,
  email: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  return publicFetch<{ success: boolean; message: string }>(
    `/api/public/${slug}/cancel`,
    {
      method: "POST",
      body: JSON.stringify({ confirmationCode, email, reason }),
    }
  );
}

export function getIcsDownloadUrl(slug: string, confirmationCode: string): string {
  return `${API_BASE}/api/public/${slug}/appointment/${confirmationCode}/ics`;
}
